import { createProvider } from "../Common/Provider"
import {
    APIAppTemplateModel,
    getInputSchema,
    getOutputSchema
} from "../../../Models/API/AppTemplateModel"
import {
    APIJobListParams,
    JobOrderBy,
    JobSortBy,
    JobStatus,
    APIJobModel,
    JobResponseModel
} from "../../../Models/API/JobModel"
import { getJobList } from "../../../Actions/API/Job/JobActions"
import { getItemList } from "../../../Models/Client/RemoteData"
import React, { ReactNode } from "react"
import { JobOutputProvider } from "../JobOutput/JobOutput"
import {
    APIAppInstanceModel,
    getLastCompletedJobID
} from "../../../Models/API/AppInstanceModel"
import { CurrentJobProvider } from "../CurrentJob/CurrentJob"
import { Result } from "../../../Models/Utility/Result"
import { FetchState, hasValue } from "../../../Models/Client/FetchState"
import { findExamples } from "../../../Helpers/Schema/Schema"

export interface ExampleJobProviderProps {
    instanceID: string
}

export interface ExampleData {
    input: any
    output?: any
    isValidJob: boolean
}

export interface ExampleDataProviderProps {
    instance: APIAppInstanceModel
    template: APIAppTemplateModel
    skipOutput?: boolean
    render: (data?: ExampleData) => ReactNode
}

const params: APIJobListParams = {
    orderBy: JobOrderBy.desc,
    sortBy: JobSortBy.completed,
    statusIn: [JobStatus.COMPLETED]
}

const ExampleJobProvider = createProvider<
    APIJobModel[],
    ExampleJobProviderProps
>(
    (state, { instanceID }) => {
        const jobs = state.api.jobs.table[instanceID]
        if (jobs === undefined) return undefined

        return jobs.list === undefined
            ? undefined
            : JSON.stringify(jobs.list.params) === JSON.stringify(params)
            ? getItemList(jobs)
            : undefined
    },
    ({ instanceID }) => getJobList(instanceID, params, 0, 1)
)

export const ExampleDataProvider = ({
    instance,
    template,
    skipOutput = false,
    render
}: ExampleDataProviderProps) => {
    const templateExample = () => {
        const isValidJob = false
        const input = findExamples(
            getInputSchema(template, false).schema,
            true
        )[0]
        if (!input) return undefined
        if (skipOutput) return { input, isValidJob }
        const output = findExamples(
            getOutputSchema(template, false).schema,
            true
        )[0]
        if (!output) return undefined
        return { input, output, isValidJob }
    }

    const child = (data: FetchState<APIJobModel>) => {
        if (!hasValue(data)) {
            return render(templateExample())
        } else {
            if (skipOutput)
                return render({
                    input: data.value.input,
                    isValidJob: true
                })

            const outputChild = (info: FetchState<JobResponseModel>) => {
                if (!hasValue(info)) {
                    return render(templateExample())
                }

                return render({
                    input: data.value.input,
                    output: info.value.response,
                    isValidJob: true
                })
            }
            // get job output if required
            return (
                <JobOutputProvider
                    appID={instance.uuid}
                    jobID={data.value.id}
                    render={outputChild}
                />
            )
        }
    }

    const lastCompleted = getLastCompletedJobID(instance)

    if (lastCompleted !== undefined) {
        return (
            <CurrentJobProvider
                appID={instance.uuid}
                jobID={lastCompleted}
                render={child}
            />
        )
    } else {
        // if we don't have "stats" from current instance"
        const childDefer = (data?: APIJobModel[] | undefined) => {
            if (
                data === undefined ||
                data.length === 0 ||
                data[0].status !== JobStatus.COMPLETED
            ) {
                return render(templateExample())
            }
            return child(Result.ok(data[0]))
        }
        return (
            <ExampleJobProvider
                render={childDefer}
                instanceID={instance.uuid}
            />
        )
    }
}
